import type { CapacityContributionType } from '@navio/nsdk';

type SlotCapacityContribution = {
  id: number;
  name: string;
  capabilityNames: string[];
  contributionType?: CapacityContributionType;
};

/**
 * Capacity summary for a time slot.
 * Counts are seat-equivalents (see supplySeatCountsFromContribution).
 */
export type SlotCapacityInfo = {
  totalRemainingSeconds?: number | null;
  personalCount: number;
  baselineCount: number;
  contributions: SlotCapacityContribution[];
};

const emptySlotCapacityInfo = (): SlotCapacityInfo => ({
  personalCount: 0,
  baselineCount: 0,
  contributions: []
});

/** Identity key for joining slot rows with their contribution-detail rows. */
export const slotSupplyKey = (slot: { workstationId?: number | null; start: string; end: string }) =>
  `${slot.workstationId ?? ''}|${slot.start}|${slot.end}`;

/** Wall-clock window key — same start/end across bays collapses to one tile. */
const wallClockKey = (slot: { start: string; end: string }) => `${slot.start}|${slot.end}`;

/** Combine supply counts when multiple workstations share the same time window. */
export const mergeSlotCapacity = (current: SlotCapacityInfo | undefined, next: SlotCapacityInfo): SlotCapacityInfo => {
  if (!current) {
    return next;
  }

  const contributionById = new Map(current.contributions.map((c) => [c.id, c]));
  for (const contribution of next.contributions) {
    contributionById.set(contribution.id, contribution);
  }

  return {
    personalCount: current.personalCount + next.personalCount,
    baselineCount: current.baselineCount + next.baselineCount,
    totalRemainingSeconds:
      current.totalRemainingSeconds != null || next.totalRemainingSeconds != null
        ? (current.totalRemainingSeconds ?? 0) + (next.totalRemainingSeconds ?? 0)
        : undefined,
    contributions: [...contributionById.values()]
  };
};

/** One bay row that can participate in wall-clock collapse. */
type CollapsibleSlotInput = {
  start: string;
  end: string;
  workstationId?: number | null;
  capacity?: SlotCapacityInfo;
};

/**
 * One UI tile after collapsing multi-bay rows that share the same start/end.
 * `pinned` is the first bay in input order (auto-assignment for create/reserve).
 * Generic `T` preserves caller payload (e.g. full API slot record) on `pinned`.
 */
type CollapsedWallClockTile<T extends CollapsibleSlotInput = CollapsibleSlotInput> = {
  id: string;
  start: string;
  end: string;
  availableCount: number;
  capacity?: SlotCapacityInfo;
  pinned: T;
};

/**
 * Collapse multi-bay capacity rows that share a wall-clock window.
 * First bay in input order is pinned for create (workstation auto-assignment).
 * Extra fields on each input are retained on `pinned`.
 */
export const collapseSlotsByWallClock = <T extends CollapsibleSlotInput>(slots: T[]): CollapsedWallClockTile<T>[] => {
  const collapsed = new Map<string, CollapsedWallClockTile<T>>();

  for (const slot of slots) {
    const key = wallClockKey(slot);
    const existing = collapsed.get(key);
    if (!existing) {
      collapsed.set(key, {
        id: key,
        start: slot.start,
        end: slot.end,
        availableCount: 1,
        capacity: slot.capacity,
        pinned: slot
      });
      continue;
    }

    existing.availableCount += 1;
    if (slot.capacity) {
      existing.capacity = mergeSlotCapacity(existing.capacity, slot.capacity);
    }
  }

  return [...collapsed.values()];
};

/** Collapsed wall-clock tile ready for calendar grouping. */
type CapacityUiSlotInput = {
  id: string;
  label: string;
  availableCount: number;
  capacity?: SlotCapacityInfo;
  dateKey: string;
};

type CapacityUiSlot = {
  id: string;
  label: string;
  availableCount: number;
  capacity?: SlotCapacityInfo;
  totalWorkstations?: number;
};

type CapacityUiDay = {
  dateKey: string;
  slots: CapacityUiSlot[];
};

/**
 * Group collapsed tiles into days by `dateKey`.
 * When `orderedDateKeys` is set, only those keys appear (in that order); otherwise
 * keys are sorted lexicographically.
 */
export const groupCapacityUiSlotsByDate = (
  slots: CapacityUiSlotInput[],
  options?: { totalWorkstations?: number; orderedDateKeys?: string[] }
): CapacityUiDay[] => {
  const totalWorkstations = options?.totalWorkstations;
  const showWorkstationRatio = totalWorkstations != null && totalWorkstations > 1;
  const byDate = new Map<string, CapacityUiSlot[]>();

  for (const slot of slots) {
    const daySlots = byDate.get(slot.dateKey) ?? [];
    daySlots.push({
      id: slot.id,
      label: slot.label,
      availableCount: slot.availableCount,
      capacity: slot.capacity,
      totalWorkstations: showWorkstationRatio ? totalWorkstations : undefined
    });
    byDate.set(slot.dateKey, daySlots);
  }

  const keys = options?.orderedDateKeys
    ? options.orderedDateKeys.filter((k) => (byDate.get(k)?.length ?? 0) > 0)
    : [...byDate.keys()].sort();

  return keys.map((dateKey) => ({
    dateKey,
    slots: byDate.get(dateKey) ?? []
  }));
};

type SupplySeatCountStyle = 'long' | 'short';

/**
 * Shared copy for personal / baseline seat-equivalents.
 * - long: "2 workers · 3 planned" (tooltips, area lists)
 * - short: "2w · 3b" (horizon day chips)
 */
export const formatSupplySeatCounts = (
  counts: Pick<SlotCapacityInfo, 'personalCount' | 'baselineCount'>,
  style: SupplySeatCountStyle = 'long'
): string => {
  const parts: string[] = [];
  if (counts.personalCount > 0) {
    parts.push(
      style === 'short'
        ? `${counts.personalCount}w`
        : `${counts.personalCount} worker${counts.personalCount === 1 ? '' : 's'}`
    );
  }
  if (counts.baselineCount > 0) {
    parts.push(style === 'short' ? `${counts.baselineCount}p` : `${counts.baselineCount} planned`);
  }
  return parts.join(' · ');
};

type EmbeddedSlotSupply = {
  contributionCount?: number | null;
  baselineContributionCount?: number | null;
  contributions?: Array<{
    id: number;
    name: string;
    capabilityNames: string[];
    contributionType?: CapacityContributionType;
  }> | null;
};

/**
 * Capacity summary from API-embedded slot supply (stationary / delivery-window payloads).
 * Counts come from the server; contribution rows are display/debug only.
 */
export const slotCapacityFromEmbeddedSupply = (slot: EmbeddedSlotSupply): SlotCapacityInfo => {
  const personalCount = slot.contributionCount ?? 0;
  const baselineCount = slot.baselineContributionCount ?? 0;
  const contributions: SlotCapacityContribution[] = (slot.contributions ?? []).map((contribution) => ({
    id: contribution.id,
    name: contribution.name,
    capabilityNames: contribution.capabilityNames,
    contributionType: contribution.contributionType
  }));

  if (personalCount === 0 && baselineCount === 0 && contributions.length === 0) {
    return emptySlotCapacityInfo();
  }

  return { personalCount, baselineCount, contributions };
};
